import { isProvisional, reliability, splitRating } from "@rally/rating";

interface RatingLockupProps {
  r: number;
  rd: number;
  /** Change in internal rating from the last match. Omit when there is no movement yet. */
  delta?: number;
  label?: string;
  matchesPlayed?: number;
  /**
   * `hero` is the full 64px signature. `card` is the same object at a size that lets two
   * disciplines sit side by side on a phone -- the first question anyone asks their profile
   * is which of the two they are better at, and an answer you have to scroll for is a worse
   * answer. `row` is the leaderboard line.
   */
  size?: "hero" | "card" | "row";
  /**
   * Renders the fraction and reliability fill in Blue Violet instead of Sun Glare.
   *
   * Sun Glare means "live" — a settled number you can act on. A provisional rating is
   * still moving, so it gets the unsettled colour. Derived from `rd` when not passed.
   */
  provisional?: boolean;
}

/**
 * The signature element — docs/design-direction.md §5.
 *
 * Three ideas in one object: the integer is tall and the fraction sits on a highlighter
 * swipe, so three decimals read as precision rather than fuss; reliability is a row of tally
 * marks that fill in as you play, so a new player can see that we are still working them
 * out; and movement sits to the right with an arrow as well as colour.
 */
export function RatingLockup({
  r,
  rd,
  delta,
  label,
  matchesPlayed,
  size = "hero",
  provisional,
}: RatingLockupProps) {
  const { whole, fraction } = splitRating(r);
  const confidence = reliability(rd);
  const hero = size === "hero";
  const row = size === "row";
  // The leaderboard row has its own columns for reliability and match count, so it is the
  // one place the caption would be a duplicate.
  const withCaption = !row;
  const scale = { hero: [96, 50], card: [64, 34], row: [26, 17] }[size] as [number, number];
  const unsettled = provisional ?? isProvisional(rd);

  return (
    <div className={hero ? "py-1" : ""}>
      {label ? <div className={hero ? "micro mb-3" : "micro mb-2"}>{label}</div> : null}

      <div className={`flex items-end ${row ? "flex-col gap-1" : "justify-between gap-4"}`}>
        <div className="flex items-baseline">
          <span
            className="font-display"
            style={{ fontSize: scale[0], fontWeight: row ? 800 : 900, lineHeight: 0.8 }}
          >
            {whole}
          </span>
          <span
            // A row is read in a column of twenty; highlighting every fraction would turn the
            // board into a stripe. There the highlighter is spent on movement instead.
            className={`font-display ${row ? (unsettled ? "text-provisional" : "") : unsettled ? "highlight-provisional" : "highlight"}`}
            style={{
              fontSize: scale[1],
              fontWeight: row ? 800 : 900,
              lineHeight: 0.9,
              padding: row ? 0 : hero ? "2px 6px 0 4px" : "1px 4px 0 3px",
            }}
          >
            {fraction}
          </span>
        </div>

        {delta !== undefined && delta !== 0 ? <Movement delta={delta} size={size} /> : null}
      </div>

      {row ? null : <Tally confidence={confidence} hero={hero} unsettled={unsettled} />}

      {withCaption ? (
        <div
          className="font-mono mt-2.5 text-xs uppercase tracking-wider"
          style={{ color: unsettled ? "var(--color-provisional)" : "var(--color-ink-muted)" }}
        >
          {confidence.toFixed(0)}%{hero ? " reliable" : ""}
          {unsettled ? " · provisional" : ""}
          {matchesPlayed !== undefined ? ` · ${matchesPlayed} matches` : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Colour is never the only signal — roughly one man in twelve has a red-green deficiency,
 * and this is a community app where everyone can see everyone's board. A gain is on the
 * highlighter; a loss is stated plainly in the muted ink.
 */
function Movement({ delta, size }: { delta: number; size: "hero" | "card" | "row" }) {
  const up = delta > 0;
  const magnitude = (Math.abs(delta) / 200).toFixed(3).replace(/^0/, "");

  return (
    <span
      className={`font-mono shrink-0 ${up ? "highlight" : ""}`}
      style={{
        fontSize: { hero: 14, card: 12, row: 10 }[size],
        fontWeight: up ? 600 : 500,
        padding: "1px 4px",
        color: up ? undefined : "var(--color-down)",
      }}
    >
      {up ? "▲" : "▼"} {magnitude}
    </span>
  );
}

/**
 * Reliability as tally marks, the way a scorer keeps count: a stroke per step, every fifth
 * one taller. Empty for a brand new player, which says "we're still working you out" better
 * than a percentage does.
 */
export function Tally({
  confidence,
  hero = false,
  unsettled = false,
  marks = hero ? 20 : 10,
  compact = false,
}: {
  confidence: number;
  hero?: boolean;
  unsettled?: boolean;
  marks?: number;
  compact?: boolean;
}) {
  const filled = Math.round((Math.max(confidence, 0) / 100) * marks);
  const on = unsettled ? "var(--color-provisional-fill)" : "var(--color-line)";
  const height = compact ? 12 : hero ? 14 : 12;
  const width = compact ? 3 : 4;

  return (
    <div
      className={`flex items-end ${compact ? "" : hero ? "mt-3.5" : "mt-2.5"}`}
      style={{ gap: compact ? 2 : 3 }}
      role="img"
      aria-label={`Rating reliability ${confidence.toFixed(0)} percent${unsettled ? ", provisional" : ""}`}
    >
      {Array.from({ length: marks }, (_, i) => (
        <span
          key={i}
          style={{
            width,
            height: hero && (i + 1) % 5 === 0 ? height + 6 : height,
            background: i < filled ? on : "var(--color-line-muted)",
          }}
        />
      ))}
    </div>
  );
}
